/**
 * Error Handler Utility
 * Converts technical errors into user-friendly messages
 */

export const ErrorHandler = {
  
  /**
   * Get user-friendly error message
   */
  getMessage: (error) => {
    // Network/Connection Errors
    if (error.message === 'Network Error' || 
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('ERR_CONNECTION_REFUSED')) {
      return {
        title: 'Connection Failed',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        type: 'error'
      };
    }

    // Timeout Errors
    if (error.code === 'ECONNABORTED' || 
        error.message?.includes('timeout')) {
      return {
        title: 'Request Timeout',
        message: 'The request took too long to complete. Please try again.',
        type: 'error'
      };
    }

    // HTTP Status Errors
    if (error.response) {
      const status = error.response.status;
      
      switch (status) {
        case 400:
          return {
            title: 'Invalid Request',
            message: error.response.data?.message || 'Please check your input and try again.',
            type: 'error'
          };
        
        case 401:
          return {
            title: 'Authentication Required',
            message: 'Your session has expired. Please log in again.',
            type: 'warning'
          };
        
        case 403:
          return {
            title: 'Access Denied',
            message: 'You do not have permission to perform this action.',
            type: 'error'
          };
        
        case 404:
          return {
            title: 'Not Found',
            message: 'The requested resource was not found.',
            type: 'error'
          };
        
        case 409:
          return {
            title: 'Conflict',
            message: error.response.data?.message || 'This action conflicts with existing data.',
            type: 'warning'
          };
        
        case 422:
          return {
            title: 'Validation Error',
            message: error.response.data?.message || 'Please check your input and try again.',
            type: 'error'
          };
        
        case 500:
        case 502:
        case 503:
        case 504:
          return {
            title: 'Server Error',
            message: 'Something went wrong on our end. Please try again later or contact support.',
            type: 'error'
          };
        
        default:
          return {
            title: 'Error',
            message: error.response.data?.message || 'An unexpected error occurred. Please try again.',
            type: 'error'
          };
      }
    }

    // Payment-specific Errors
    if (error.message?.includes('payment') || 
        error.message?.includes('razorpay')) {
      return {
        title: 'Payment Failed',
        message: error.message || 'Unable to process payment. Please try again.',
        type: 'error'
      };
    }

    // Generic Error
    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred. Please try again.',
      type: 'error'
    };
  },

  /**
   * Log error for debugging
   */
  log: (error, context = '') => {
    console.group(`🚨 Error ${context ? `in ${context}` : ''}`);
    console.error('Error object:', error);
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    console.groupEnd();
  },

  /**
   * Check if error is a network/connection error
   */
  isNetworkError: (error) => {
    return error.message === 'Network Error' || 
           error.code === 'ERR_NETWORK' ||
           error.message?.includes('ERR_CONNECTION_REFUSED') ||
           error.code === 'ECONNABORTED';
  },

  /**
   * Check if error requires re-authentication
   */
  isAuthError: (error) => {
    return error.response?.status === 401;
  }
};

export default ErrorHandler;